import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AuthApiError, User} from '@ergeon/erg-auth';
import {AuthProviderProps} from './types';

import {getResponseStatus, reportError} from '../../../utils/reporting';
import SessionRecoveryContext from './SessionRecoveryContext';
import useSessionExpiry from './useSessionExpiry';
import useSignInKeepingEdits from './useSignInKeepingEdits';

// Expected sign-in control flow: the login card is the product behavior for
// an unauthenticated or unauthorized visitor, so these produce no event.
const EXPECTED_AUTH_REASONS: AuthApiError['reason'][] = ['not-authenticated', 'not-authorized', 'missing-token'];

// Auth outages (network/5xx → reason 'unknown') previously produced no signal
// at all. Report the underlying error so the original stack survives; never
// `authError` itself, which carries the response payload the reporter would
// stringify into the event.
//
// `retrieveUser()` wraps `getUser()` in a Promise executor, so anything that
// throws synchronously in there (corrupted `requestUser` storage, malformed
// JWT, missing config) rejects with a raw Error carrying no `reason` and no
// `.error`. Those are exactly the real failures this exists for, so fall back
// to the caught value whenever it is already an Error and only synthesize one
// when there is genuinely neither.
const getAuthCause = (rejection: unknown): unknown => {
  const {error} = rejection as AuthApiError;
  if (error) return error;
  return rejection instanceof Error ? rejection : undefined;
};

const reportAuthOutage = (rejection: unknown) => {
  const authError = rejection as AuthApiError;
  if (EXPECTED_AUTH_REASONS.includes(authError.reason)) return;
  const cause = getAuthCause(rejection);
  reportError(cause ?? new Error(`Auth user retrieve failed (${authError.reason})`), {
    operation: 'auth_user_retrieve',
    handled: true,
    extra: {
      reason: authError.reason,
      status_code: getResponseStatus(cause) ?? getResponseStatus(authError),
    },
  });
};

/**
 * Allows connecting the Auth through passing a necessary options to the child component.
 * Expect from the child component to implement needed UI.
 * @param props auth service and children as a function
 * @returns ReactNode
 */
function AuthProvider(props: AuthProviderProps) {
  const {authService, children} = props;

  const [user, setUser] = useState<User>();
  const [error, setError] = useState<AuthApiError>();
  const {sessionNoticeVisible, dismissSessionNotice, checkSessionQuietly, onUnauthorized} =
    useSessionExpiry(authService);
  const {recoveryFlushRef, signInProgress, onAuth} = useSignInKeepingEdits(authService, sessionNoticeVisible);
  const sessionRecovery = useMemo(() => ({onUnauthorized, recoveryFlushRef}), [onUnauthorized, recoveryFlushRef]);

  const getUser = useCallback(async () => {
    try {
      setUser(Object.freeze(await authService.retrieveUser()));
    } catch (error) {
      reportAuthOutage(error);
      setError(error as AuthApiError);
    }
  }, [authService]);

  const onRefresh = useCallback(() => {
    checkSessionQuietly();
    void getUser();
  }, [checkSessionQuietly, getUser]);

  useEffect(() => {
    // onMount
    if (!user) {
      void getUser();
    }
    authService.on('refresh', onRefresh);

    return () => {
      // onUnmount
      authService.off('refresh', onRefresh);
    };
  }, [authService, user]);

  return (
    <SessionRecoveryContext.Provider value={sessionRecovery}>
      {children({
        user,
        error,
        sessionNoticeVisible,
        signInProgress,
        onAuth,
        onDismissSessionNotice: dismissSessionNotice,
      })}
    </SessionRecoveryContext.Provider>
  );
}

export default AuthProvider;
